import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, RotateCcw, ShieldCheck, X } from 'lucide-react';
import useAppStore from '../../store/appStore';
import { savePePapersArchiveDoc } from '../../services/pePapersArchiveService';
import {
    buildHtmlAsPdfBlob,
    downloadHtmlAsPdf,
} from '../../utils/pe-papers/htmlDocumentPdfDownload';
import PePapersStorageHint, { PE_PAPERS_STORAGE_HINTS } from './PePapersStorageHint';
import {
    DSGVO_CONFIRMATION_TEXT,
    DSGVO_DEFAULT_TOM_NOTE_OK,
    DSGVO_DEFAULT_TOM_NOTE_OPEN,
    DSGVO_DISCLAIMER_CHECKBOX,
    DSGVO_DOC_ID,
    DSGVO_DOC_SUBTITLE,
    DSGVO_DOC_TITLE,
    DSGVO_OPEN_CASH_LABEL,
    DSGVO_PAGE2_BAND_LABEL,
    DSGVO_PROCESSES,
    DSGVO_SELF_ACCOUNTING_LABEL,
    DSGVO_TOMS,
    createDefaultDsgvoProcessStates,
    createDefaultDsgvoTomStates,
    getProcessDef,
    type DsgvoProcessState,
    type DsgvoTomState,
} from '../../utils/pe-papers/dsgvoVvtCatalog';

export interface DsgvoVvtModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialData?: any;
    onArchived?: () => void;
}

type TabId = 'prozesse' | 'toms';

const INPUT_CLASS =
    'w-full bg-pe-input dark:bg-black border border-pe-border dark:border-slate-700 rounded-lg px-3 py-2.5 text-pe-text dark:text-white text-sm focus:border-[#E6C299] outline-none';
const LABEL_CLASS =
    'text-[10px] font-semibold text-pe-muted dark:text-slate-400 uppercase tracking-wider mb-1 block';
const ERROR_RING = 'border-red-500 ring-1 ring-red-500/40';

const A4_PAGE_CLASS =
    'dsgvo-a4-page bg-white text-black mx-auto w-full max-w-[210mm] min-h-[297mm] h-auto shadow-xl rounded-sm text-left block overflow-visible box-border my-4 print:my-0 print:shadow-none print:rounded-none';

const STEP_LABELS: Record<number, string> = {
    1: 'Datenerfassung',
    2: 'Dokument & Archiv',
};

const DSGVO_DOC_CSS = `
  .dsgvo-a4-page {
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 12.5px !important;
    line-height: 1.42 !important;
    color: #0f172a !important;
    background: #ffffff !important;
    box-sizing: border-box !important;
    padding: 8mm 10mm !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .dsgvo-a4-page-2 { padding: 8mm 10mm !important; }
  .dsgvo-screen-only { display: block; }
  .dsgvo-page-label {
    font-size: 12px !important; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
    color: #64748b; margin: 0 0 6px; padding-bottom: 3px; border-bottom: 1px dashed #cbd5e1;
  }
  .dsgvo-page2-band {
    display: table; width: 100%; border-collapse: collapse;
    margin: 0 0 8px; padding: 0 0 6px;
    border-bottom: 1.5px solid #E6C299;
  }
  .dsgvo-page2-band td {
    vertical-align: middle; padding: 0; border: none; background: transparent;
    font-size: 12.5px !important; color: #475569; line-height: 1.3;
  }
  .dsgvo-page2-band td:last-child { text-align: right; white-space: nowrap; }
  .dsgvo-page2-band strong { color: #0f172a; font-weight: 700; }
  .dsgvo-header {
    width: 100%; border-collapse: collapse;
    margin: 0 0 8px; padding: 0 0 8px;
    border-bottom: 2px solid #E6C299;
  }
  .dsgvo-header td { vertical-align: top; padding: 0; border: none; background: transparent; }
  .dsgvo-title {
    font-size: 14px !important; font-weight: 800 !important; line-height: 1.2 !important;
    margin: 0 0 2px !important; text-align: left !important; color: #0f172a !important;
    text-transform: none !important; letter-spacing: 0 !important;
  }
  .dsgvo-title-pe { font-style: italic; color: #E6C299 !important; font-weight: 800; }
  .dsgvo-title-sub {
    display: block; font-size: 12px !important; font-weight: 700 !important;
    color: #0f172a !important; margin-top: 2px;
  }
  .dsgvo-salon-line { font-size: 11px !important; font-weight: 600; color: #334155; margin: 2px 0 0; }
  .dsgvo-header-meta {
    width: 170px; text-align: right; font-size: 12.5px !important; color: #475569; line-height: 1.3;
    white-space: nowrap; padding-left: 10px !important;
  }
  .dsgvo-header-meta p { margin: 0 0 2px !important; text-align: right !important; font-size: 12.5px !important; }
  .dsgvo-header-meta strong { color: #0f172a; font-weight: 700; }
  .dsgvo-lead {
    font-size: 12px !important; color: #475569; margin: 0 0 8px; line-height: 1.42; text-align: justify;
  }
  .dsgvo-box {
    border: 1px solid #cbd5e1 !important;
    border-radius: 5px !important;
    background: #f1f5f9 !important;
    padding: 8px 10px !important;
    margin: 0 0 7px !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .dsgvo-h {
    font-size: 11px !important; font-weight: 800 !important; text-transform: uppercase;
    letter-spacing: 0.04em; border-bottom: 1.5px solid #E6C299; padding-bottom: 3px;
    margin: 0 0 6px !important; color: #0f172a !important; text-align: left !important;
    background: transparent !important;
  }
  .dsgvo-process-block { margin: 0 0 6px !important; }
  .dsgvo-process-title {
    font-size: 12px !important; font-weight: 800 !important; color: #0f172a !important;
    margin: 0 0 3px !important; text-align: left !important;
  }
  .dsgvo-meta-table {
    width: 100% !important;
    border-collapse: collapse !important;
    table-layout: fixed !important;
    margin: 0 !important;
  }
  .dsgvo-meta-table th {
    width: 128px !important;
    max-width: 128px !important;
    vertical-align: top !important;
    text-align: left !important;
    font-size: 12.5px !important;
    font-weight: 700 !important;
    color: #334155 !important;
    padding: 2px 8px 2px 0 !important;
    border-bottom: 1px solid #e2e8f0 !important;
    white-space: nowrap !important;
  }
  .dsgvo-meta-table td {
    vertical-align: top !important;
    text-align: left !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    font-size: 12.5px !important;
    font-weight: 400 !important;
    color: #0f172a !important;
    padding: 2px 0 !important;
    border-bottom: 1px solid #e2e8f0 !important;
    line-height: 1.3 !important;
  }
  .dsgvo-row { margin: 0 0 4px !important; font-size: 12px !important; color: #1e293b !important; line-height: 1.4; }
  .dsgvo-row strong { color: #0f172a !important; font-weight: 700; }
  .dsgvo-legal {
    font-size: 12px !important; text-align: justify; margin: 0 0 6px !important;
    color: #334155 !important; line-height: 1.35 !important;
  }
  .dsgvo-status-ok {
    font-size: 12px !important; font-weight: 700 !important; color: #15803d !important; margin: 2px 0 0 !important;
  }
  .dsgvo-inactive {
    font-size: 12px !important; color: #64748b !important; margin: 0 0 2px !important; line-height: 1.3;
  }
  .dsgvo-tom-table {
    width: 100% !important; border-collapse: collapse !important; margin: 0 !important;
    font-size: 12.5px !important; table-layout: fixed !important;
  }
  .dsgvo-tom-table thead { display: table-header-group !important; }
  .dsgvo-tom-table tr {
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }
  .dsgvo-tom-table th {
    background: #f1f5f9 !important; color: #0f172a !important; font-weight: 700 !important;
    text-align: left !important; padding: 5px 6px !important; border-bottom: 1px solid #cbd5e1 !important;
  }
  .dsgvo-tom-table td {
    padding: 5px 6px !important; border-bottom: 1px solid #e2e8f0 !important;
    vertical-align: top !important; color: #1e293b !important;
    white-space: normal !important;
    word-break: normal !important;
    overflow-wrap: break-word !important;
    line-height: 1.35 !important;
  }
  .dsgvo-tom-table td.dsgvo-tom-status {
    white-space: nowrap !important;
    width: 18% !important;
  }
  .dsgvo-tom-table td.dsgvo-tom-note { width: 24% !important; }
  .dsgvo-tom-table td.dsgvo-tom-measure { width: 58% !important; }
  .dsgvo-tom-ok { color: #15803d !important; font-weight: 700 !important; }
  .dsgvo-tom-open { color: #b45309 !important; font-weight: 700 !important; }
  .dsgvo-sig-wrap {
    margin-top: 8px;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }
  .dsgvo-sig-grid {
    display: table !important; width: 100% !important; table-layout: fixed !important;
    margin-top: 8px !important; border-collapse: separate !important; border-spacing: 16px 0 !important;
  }
  .dsgvo-sig-col {
    display: table-cell !important; width: 50% !important; vertical-align: top !important;
    padding: 0 !important;
  }
  .dsgvo-sig-label {
    margin: 0 0 4px !important; font-size: 12.5px !important; font-weight: 700 !important;
    color: #0f172a !important; text-transform: uppercase; letter-spacing: 0.03em;
  }
  .dsgvo-sig-value {
    margin: 0 0 0 !important; font-size: 11px !important; color: #1e293b !important;
  }
  .dsgvo-stamp-box {
    border: none !important;
    border-bottom: 2px solid #1e293b !important;
    background: #ffffff !important;
    height: 40px !important;
    margin: 6px 0 0 !important;
    border-radius: 0 !important;
  }
  .dsgvo-sig-name {
    margin: 0 !important; font-size: 12.5px !important; color: #334155 !important;
    font-weight: 600 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.04em !important;
  }
`;

/**
 * PDF-Capture: Seiten-Padding aus DOC_CSS behalten (10mm/11mm).
 * Früher padding:0 → Seite 2 ohne Rand (Style-Tag lag nur in Seite-1-Sektion).
 */
const DSGVO_PDF_CSS = `
  ${DSGVO_DOC_CSS}
  .dsgvo-screen-only { display: none !important; }
  .dsgvo-a4-page, .dsgvo-a4-page-2 {
    min-height: 0 !important;
    margin: 0 !important;
    padding: 8mm 10mm !important;
    box-shadow: none !important;
    max-width: none !important;
    border-radius: 0 !important;
  }
`;

/** Capture-sicher: echter 1px-Balken (kein borderBottom auf leerem Div) */
const DSGVO_SIG_LINE_STYLE: React.CSSProperties = {
    height: '1px',
    backgroundColor: '#0f172a',
    width: '100%',
    marginTop: '28px',
    marginBottom: '6px',
};

const todayIso = () => new Date().toISOString().split('T')[0];

const formatGermanDate = (iso: string) => {
    if (!iso) return '____________';
    const [y, m, d] = iso.split('-');
    if (!y || !m || !d) return iso;
    return `${d}.${m}.${y}`;
};

const buildInitialProcesses = (isSoloBusiness?: boolean): DsgvoProcessState[] => {
    const defaults = createDefaultDsgvoProcessStates();
    if (isSoloBusiness) {
        return defaults.map((p) => (p.id === 'personal' ? { ...p, active: false } : p));
    }
    return defaults;
};

/** Einheitliches Label|Wert-Raster für alle Verarbeitungsblöcke (HTML + PDF-Capture). */
const renderProcessMetaTable = (rows: Array<[string, string]>) => (
    <table className="dsgvo-meta-table">
        <tbody>
            {rows.map(([label, value], idx) => (
                <tr key={`${label}-${idx}`}>
                    <th scope="row">{label}</th>
                    <td>{value}</td>
                </tr>
            ))}
        </tbody>
    </table>
);

const ToggleRow: React.FC<{
    title: string;
    sub: string;
    checked: boolean;
    onChange: (value: boolean) => void;
    note: string;
    onNoteChange: (value: string) => void;
}> = ({ title, sub, checked, onChange, note, onNoteChange }) => (
    <div className="rounded-xl border border-pe-border dark:border-slate-700 bg-pe-elevated dark:bg-[#0a0a0a] px-3 py-3">
        <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
                <p className="text-sm font-semibold text-pe-text dark:text-slate-50">{title}</p>
                <p className="mt-0.5 text-xs leading-relaxed text-pe-muted dark:text-slate-400">{sub}</p>
            </div>
            <button
                type="button"
                role="switch"
                aria-checked={checked}
                onClick={() => onChange(!checked)}
                className={`relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition-colors ${
                    checked ? 'bg-emerald-500' : 'bg-slate-600'
                }`}
            >
                <span
                    className={`absolute top-0.5 left-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-white shadow transition-transform ${
                        checked ? 'translate-x-5' : 'translate-x-0'
                    }`}
                >
                    {checked ? <Check className="h-3 w-3 text-emerald-600" /> : null}
                </span>
            </button>
        </div>
        {!checked ? (
            <label className="mt-2 block">
                <span className={LABEL_CLASS}>Bemerkung (optional)</span>
                <textarea
                    value={note}
                    onChange={(e) => onNoteChange(e.target.value)}
                    rows={2}
                    placeholder="Grund / Ersatzmaßnahme…"
                    className={`${INPUT_CLASS} resize-y`}
                />
            </label>
        ) : null}
    </div>
);

const DsgvoVvtModal: React.FC<DsgvoVvtModalProps> = ({
    isOpen,
    onClose,
    initialData,
    onArchived,
}) => {
    const clientData = useAppStore((s: any) => s.clientData);
    const [currentStep, setCurrentStep] = useState(1);
    const [activeTab, setActiveTab] = useState<TabId>('prozesse');
    const [processes, setProcesses] = useState<DsgvoProcessState[]>(createDefaultDsgvoProcessStates);
    const [toms, setToms] = useState<DsgvoTomState[]>(createDefaultDsgvoTomStates);
    const [assessedAt, setAssessedAt] = useState(todayIso);
    const [confirmerName, setConfirmerName] = useState('');
    const [cashSoftwareName, setCashSoftwareName] = useState('');
    const [taxAdvisorName, setTaxAdvisorName] = useState('');
    const [isOpenCashRegister, setIsOpenCashRegister] = useState(false);
    const [isSelfAccounting, setIsSelfAccounting] = useState(false);
    const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
    const [validationError, setValidationError] = useState('');
    const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isPdfDownloading, setIsPdfDownloading] = useState(false);
    const [archiveFeedback, setArchiveFeedback] = useState<'idle' | 'success' | 'error' | 'draft'>(
        'idle'
    );
    const [archiveErrorDetail, setArchiveErrorDetail] = useState('');
    const [archiveDocId, setArchiveDocId] = useState<string | null>(null);
    const printContainerRef = useRef<HTMLDivElement>(null);

    const isActionLocked = !disclaimerAccepted;

    useEffect(() => {
        if (!isOpen) return;
        const scrollY = window.scrollY;
        const prev = {
            overflow: document.body.style.overflow,
            position: document.body.style.position,
            top: document.body.style.top,
            width: document.body.style.width,
            htmlOverflow: document.documentElement.style.overflow,
        };
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';
        return () => {
            document.documentElement.style.overflow = prev.htmlOverflow;
            document.body.style.overflow = prev.overflow;
            document.body.style.position = prev.position;
            document.body.style.top = prev.top;
            document.body.style.width = prev.width;
            window.scrollTo(0, scrollY);
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            setCurrentStep(1);
            setInvalidFields(new Set());
            setValidationError('');
            setDisclaimerAccepted(false);
            setIsSaving(false);
            setArchiveFeedback('idle');
            setArchiveErrorDetail('');
            setArchiveDocId(null);
            return;
        }

        setValidationError('');
        setDisclaimerAccepted(false);
        setIsSaving(false);
        setArchiveFeedback('idle');
        setArchiveErrorDetail('');
        setArchiveDocId(initialData?.id || null);
        setCurrentStep(1);
        setInvalidFields(new Set());

        if (initialData) {
            if (Array.isArray(initialData.processes) && initialData.processes.length > 0) {
                setProcesses(initialData.processes);
            } else if (
                Array.isArray(initialData.payload?.processes) &&
                initialData.payload.processes.length > 0
            ) {
                setProcesses(initialData.payload.processes);
            } else {
                setProcesses(buildInitialProcesses(!!clientData?.isSoloBusiness));
            }

            if (Array.isArray(initialData.toms) && initialData.toms.length > 0) {
                setToms(initialData.toms);
            } else if (Array.isArray(initialData.payload?.toms) && initialData.payload.toms.length > 0) {
                setToms(initialData.payload.toms);
            } else {
                setToms(createDefaultDsgvoTomStates());
            }

            const p = initialData.payload || initialData;
            setAssessedAt(String(p.assessedAtIso || initialData.assessedAtIso || todayIso()));
            setConfirmerName(
                String(p.confirmerName || initialData.confirmerName || clientData?.unternehmername || '').trim()
            );
            setCashSoftwareName(String(p.cashSoftwareName || initialData.cashSoftwareName || ''));
            setTaxAdvisorName(String(p.taxAdvisorName || initialData.taxAdvisorName || ''));
            setIsOpenCashRegister(Boolean(p.isOpenCashRegister ?? initialData.isOpenCashRegister));
            setIsSelfAccounting(Boolean(p.isSelfAccounting ?? initialData.isSelfAccounting));
            setActiveTab(
                p.activeTab === 'toms' || initialData.activeTab === 'toms' ? 'toms' : 'prozesse'
            );
            const step = Number(initialData.currentStep);
            if (Number.isFinite(step) && step >= 1 && step <= 2) setCurrentStep(step);
            if (initialData.confirmedDisclaimer) setDisclaimerAccepted(true);
        } else {
            setProcesses(buildInitialProcesses(!!clientData?.isSoloBusiness));
            setToms(createDefaultDsgvoTomStates());
            setAssessedAt(todayIso());
            setConfirmerName(String(clientData?.unternehmername || '').trim());
            setCashSoftwareName('');
            setTaxAdvisorName('');
            setIsOpenCashRegister(false);
            setIsSelfAccounting(false);
            setActiveTab('prozesse');
        }
    }, [isOpen, initialData, clientData?.unternehmername, clientData?.isSoloBusiness]);

    if (!isOpen) return null;

    const salonName = clientData?.salonname || clientData?.firmenname || 'Salon';
    const salonOwner = clientData?.unternehmername || 'Inhaber';
    const street = String(clientData?.strasse || '').trim();
    const zip = String(clientData?.plz || '').trim();
    const city = String(clientData?.ort || '').trim();
    const email = String(clientData?.mailadresse || '').trim();
    const phone = String(clientData?.whatsappNr || '').trim();
    const addressLine = [street, [zip, city].filter(Boolean).join(' ')].filter(Boolean).join(', ');

    const kasseActive = processes.find((p) => p.id === 'kasse')?.active === true;
    const personalActive = processes.find((p) => p.id === 'personal')?.active === true;

    const resolvedCashLabel = isOpenCashRegister
        ? DSGVO_OPEN_CASH_LABEL
        : cashSoftwareName.trim();
    const resolvedTaxLabel = isSelfAccounting
        ? DSGVO_SELF_ACCOUNTING_LABEL
        : taxAdvisorName.trim();

    const setProcessActive = (id: string, active: boolean) => {
        setProcesses((prev) =>
            prev.map((p) => (p.id === id ? { ...p, active, note: active ? '' : p.note } : p))
        );
        setValidationError('');
        setInvalidFields((prev) => {
            const next = new Set(prev);
            next.delete('cashSoftwareName');
            next.delete('taxAdvisorName');
            return next;
        });
    };

    const setProcessNote = (id: string, note: string) => {
        setProcesses((prev) => prev.map((p) => (p.id === id ? { ...p, note } : p)));
    };

    const setTomImplemented = (id: string, implemented: boolean) => {
        setToms((prev) =>
            prev.map((t) =>
                t.id === id ? { ...t, implemented, note: implemented ? '' : t.note } : t
            )
        );
    };

    const setTomNote = (id: string, note: string) => {
        setToms((prev) => prev.map((t) => (t.id === id ? { ...t, note } : t)));
    };

    const confirmStandards = () => {
        setProcesses(buildInitialProcesses(!!clientData?.isSoloBusiness));
        setToms(createDefaultDsgvoTomStates());
        setIsOpenCashRegister(false);
        setIsSelfAccounting(false);
        setValidationError('');
        setInvalidFields(new Set());
    };

    const validateStep1 = (): string[] => {
        const err: string[] = [];
        if (!String(confirmerName || '').trim()) err.push('confirmerName');
        if (!assessedAt) err.push('assessedAt');
        if (kasseActive && !isOpenCashRegister && !String(cashSoftwareName || '').trim()) {
            err.push('cashSoftwareName');
        }
        if (personalActive && !isSelfAccounting && !String(taxAdvisorName || '').trim()) {
            err.push('taxAdvisorName');
        }
        return err;
    };

    const handleWeiter = () => {
        const err = validateStep1();
        setInvalidFields(new Set(err));
        if (err.length) {
            setValidationError('Bitte fülle die markierten Pflichtfelder aus.');
            setActiveTab('prozesse');
            return;
        }
        setValidationError('');
        setCurrentStep(2);
    };

    const handleClose = () => onClose();

    const getDocumentHtml = () =>
        printContainerRef.current?.innerHTML ||
        document.getElementById('pepapers-dsgvo-print')?.innerHTML ||
        '';

    const buildPdfOptions = (html: string) => {
        const dateIso = assessedAt || new Date().toISOString().slice(0, 10);
        return {
            html,
            filename: `DSGVO_Verfahrensdokumentation_${dateIso}.pdf`,
            extraCss: DSGVO_PDF_CSS,
            splitPrintBreaks: true as const,
            scrubMode: 'minimal' as const,
            fitSectionsToPage: true,
            letterheadOnBreaks: false,
            capturePadPx: 0,
        };
    };

    /** Druck = exakt dasselbe PDF wie Download (kein separates HTML-Print-Layout). */
    const handlePrint = async () => {
        if (isActionLocked || isPdfDownloading) return;
        const html = getDocumentHtml();
        if (!html.trim()) {
            window.alert('Dokumentinhalt fehlt — bitte Vorschau prüfen.');
            return;
        }
        setIsPdfDownloading(true);
        try {
            const blob = await buildHtmlAsPdfBlob(buildPdfOptions(html));
            const url = URL.createObjectURL(blob);
            const iframe = document.createElement('iframe');
            iframe.setAttribute('title', 'DSGVO Druck');
            iframe.style.cssText =
                'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
            document.body.appendChild(iframe);
            const cleanup = () => {
                window.setTimeout(() => {
                    iframe.remove();
                    URL.revokeObjectURL(url);
                }, 1500);
            };
            iframe.onload = () => {
                try {
                    iframe.contentWindow?.focus();
                    iframe.contentWindow?.print();
                } finally {
                    cleanup();
                }
            };
            iframe.src = url;
        } catch (err) {
            console.error('DSGVO Druck fehlgeschlagen:', err);
            window.alert('Druckvorschau konnte nicht erstellt werden.');
        } finally {
            setIsPdfDownloading(false);
        }
    };

    const handleDownloadPdf = async () => {
        if (isActionLocked || isPdfDownloading) return;
        const html = getDocumentHtml();
        if (!html.trim()) {
            window.alert('Dokumentinhalt fehlt — bitte Vorschau prüfen.');
            return;
        }
        setIsPdfDownloading(true);
        try {
            await downloadHtmlAsPdf(buildPdfOptions(html));
        } catch (err) {
            console.error('DSGVO PDF fehlgeschlagen:', err);
            window.alert('PDF konnte nicht heruntergeladen werden.');
        } finally {
            setIsPdfDownloading(false);
        }
    };

    const handleArchive = async (asDraft = false) => {
        if (isSaving) return;
        if (!asDraft && isActionLocked) return;
        if (!asDraft) {
            const err = validateStep1();
            if (err.length) {
                setInvalidFields(new Set(err));
                setValidationError('Bitte fülle die markierten Pflichtfelder aus.');
                setCurrentStep(1);
                return;
            }
        }
        setIsSaving(true);
        setArchiveFeedback('idle');
        setArchiveErrorDetail('');
        const htmlContent =
            printContainerRef.current?.innerHTML ||
            document.getElementById('pepapers-dsgvo-print')?.innerHTML ||
            '';
        try {
            const htmlSafe = htmlContent && htmlContent.length < 850_000 ? htmlContent : '';
            const id = await savePePapersArchiveDoc({
                id: archiveDocId,
                documentType: 'dsgvo-verfahrensdokumentation',
                status: asDraft ? 'entwurf' : 'final',
                salonName,
                mitarbeiterName: confirmerName.trim() || salonOwner,
                confirmedDisclaimer: asDraft ? disclaimerAccepted : true,
                currentStep,
                payload: {
                    processes,
                    toms,
                    assessedAtIso: assessedAt,
                    confirmerName: confirmerName.trim(),
                    cashSoftwareName: cashSoftwareName.trim(),
                    taxAdvisorName: taxAdvisorName.trim(),
                    isOpenCashRegister,
                    isSelfAccounting,
                    activeTab,
                    documentId: DSGVO_DOC_ID,
                    generatedHtml: htmlSafe,
                },
            });
            setArchiveDocId(id);
            setArchiveFeedback(asDraft ? 'draft' : 'success');
            onArchived?.();
            if (asDraft) setTimeout(() => onClose(), 600);
            else setTimeout(() => setArchiveFeedback('idle'), 3000);
        } catch (e) {
            console.error('Verfahrensdokumentation Archivierung fehlgeschlagen:', e);
            setArchiveErrorDetail(e instanceof Error ? e.message : 'Unbekannter Fehler');
            setArchiveFeedback('error');
        } finally {
            setIsSaving(false);
        }
    };

    const fieldClass = (id: string) =>
        `${INPUT_CLASS} ${invalidFields.has(id) ? ERROR_RING : ''}`;

    const btnClass = (base: string) =>
        `${base} ${isActionLocked || isSaving ? 'opacity-50 cursor-not-allowed' : ''}`;

    const activeProcesses = processes.filter((p) => p.active);
    const inactiveProcesses = processes.filter((p) => !p.active);

    const renderDocument = () => (
        <div className="w-full h-auto overflow-visible">
            {/* Style außerhalb des Capture-Roots → beide Seiten bekommen denselben Rand via extraCss */}
            <style>{DSGVO_DOC_CSS}</style>
            <div
                ref={printContainerRef}
                id="pepapers-dsgvo-print"
                className="w-full h-auto overflow-visible"
            >
            <div className={A4_PAGE_CLASS}>
                <p className="dsgvo-screen-only dsgvo-page-label">Seite 1 von 2</p>
                <table className="dsgvo-header">
                    <tbody>
                        <tr>
                            <td>
                                <h1 className="dsgvo-title">
                                    <span className="dsgvo-title-pe">PE</span>Papers
                                    <span className="dsgvo-title-sub">DSGVO-Verfahrensdokumentation</span>
                                </h1>
                                {salonName ? <p className="dsgvo-salon-line">{salonName}</p> : null}
                            </td>
                            <td className="dsgvo-header-meta">
                                <p>
                                    Dokument: <strong>{DSGVO_DOC_ID}</strong>
                                </p>
                                <p>
                                    Prüfdatum: <strong>{formatGermanDate(assessedAt)}</strong>
                                </p>
                                <p>
                                    Bestätigt:{' '}
                                    <strong>{confirmerName.trim() || salonOwner || '—'}</strong>
                                </p>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <p className="dsgvo-lead">
                    <strong>{DSGVO_DOC_TITLE}</strong>
                    <br />
                    {DSGVO_DOC_SUBTITLE}
                </p>

                <div className="dsgvo-box">
                    <h3 className="dsgvo-h">Verantwortliche Stelle</h3>
                    <p className="dsgvo-row">
                        <strong>Salon / Firma:</strong> {salonName || '—'}
                    </p>
                    <p className="dsgvo-row">
                        <strong>Verantwortliche/r:</strong> {salonOwner || '—'}
                    </p>
                    <p className="dsgvo-row">
                        <strong>Adresse:</strong> {addressLine || '—'}
                    </p>
                    <p className="dsgvo-row">
                        <strong>E-Mail:</strong> {email || '—'}
                    </p>
                    <p className="dsgvo-row">
                        <strong>Telefon:</strong> {phone || '—'}
                    </p>
                </div>

                <div className="dsgvo-box">
                    <h3 className="dsgvo-h">Teil A – Verarbeitungstätigkeiten (Art. 30 DSGVO)</h3>
                    {activeProcesses.length === 0 ? (
                        <p className="dsgvo-legal">Keine aktiven Verarbeitungstätigkeiten ausgewählt.</p>
                    ) : (
                        activeProcesses.map((state) => {
                            const def = getProcessDef(state.id);
                            if (!def) return null;
                            const metaRows: Array<[string, string]> = [
                                ['Zweck', def.purpose],
                                ['Rechtsgrundlage', def.legalBasis],
                                ['Datenkategorien', def.dataCategories],
                                ['Löschfrist', def.retention],
                            ];
                            if (def.recipients) metaRows.push(['Empfänger', def.recipients]);
                            if (def.requiresCashSoftware) {
                                metaRows.push([
                                    'Eingesetztes System',
                                    resolvedCashLabel || 'Nicht angegeben',
                                ]);
                            }
                            if (def.requiresTaxAdvisor) {
                                metaRows.push([
                                    'Eingesetztes System',
                                    resolvedTaxLabel || 'Nicht angegeben',
                                ]);
                            }
                            if (state.note?.trim()) {
                                metaRows.push(['Bemerkung', state.note.trim()]);
                            }
                            return (
                                <div key={state.id} className="dsgvo-process-block">
                                    <p className="dsgvo-process-title">{def.title}</p>
                                    {renderProcessMetaTable(metaRows)}
                                    <p className="dsgvo-status-ok">Status: Aktiv / Dokumentiert</p>
                                </div>
                            );
                        })
                    )}
                    {inactiveProcesses.length > 0 ? (
                        <div style={{ marginTop: 4 }}>
                            <p className="dsgvo-row">
                                <strong>Nicht aktive / nicht zutreffende Tätigkeiten:</strong>
                            </p>
                            {inactiveProcesses.map((state) => {
                                const def = getProcessDef(state.id);
                                if (!def) return null;
                                const note = state.note?.trim() ? ` – ${state.note.trim()}` : '';
                                return (
                                    <p key={state.id} className="dsgvo-inactive">
                                        • {def.title}
                                        {note}
                                    </p>
                                );
                            })}
                        </div>
                    ) : null}
                </div>
            </div>

            <div className={`print-break pepapers-print-break dsgvo-a4-page-2 ${A4_PAGE_CLASS}`}>
                <p className="dsgvo-screen-only dsgvo-page-label">Seite 2 von 2</p>
                <table className="dsgvo-page2-band">
                    <tbody>
                        <tr>
                            <td>
                                <strong>{DSGVO_DOC_ID}</strong>
                                {' | '}
                                {salonName || 'Salon'}
                            </td>
                            <td>{DSGVO_PAGE2_BAND_LABEL}</td>
                        </tr>
                    </tbody>
                </table>

                <div className="dsgvo-box">
                    <h3 className="dsgvo-h">
                        Teil B – Technische &amp; Organisatorische Maßnahmen (Art. 32 DSGVO)
                    </h3>
                    <table className="dsgvo-tom-table">
                        <thead>
                            <tr>
                                <th style={{ width: '58%' }}>Maßnahme</th>
                                <th style={{ width: '18%' }}>Status</th>
                                <th style={{ width: '24%' }}>Bemerkung</th>
                            </tr>
                        </thead>
                        <tbody>
                            {toms.map((state) => {
                                const def = DSGVO_TOMS.find((t) => t.id === state.id);
                                if (!def) return null;
                                const note =
                                    state.note?.trim() ||
                                    (state.implemented
                                        ? DSGVO_DEFAULT_TOM_NOTE_OK
                                        : DSGVO_DEFAULT_TOM_NOTE_OPEN);
                                return (
                                    <tr key={state.id}>
                                        <td className="dsgvo-tom-measure">
                                            <strong>{def.title}:</strong>{' '}
                                            <span>{def.description}</span>
                                        </td>
                                        <td
                                            className={`dsgvo-tom-status ${
                                                state.implemented ? 'dsgvo-tom-ok' : 'dsgvo-tom-open'
                                            }`}
                                        >
                                            {state.implemented ? 'Umgesetzt' : 'Nicht umgesetzt'}
                                        </td>
                                        <td className="dsgvo-tom-note">{note}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="dsgvo-box dsgvo-sig-wrap">
                    <h3 className="dsgvo-h">Bestätigung &amp; Unterschrift</h3>
                    <p className="dsgvo-legal">{DSGVO_CONFIRMATION_TEXT}</p>
                    <div className="dsgvo-sig-grid">
                        <div className="dsgvo-sig-col">
                            <p className="dsgvo-sig-label">Ort, Datum:</p>
                            <p className="dsgvo-sig-value">
                                {city
                                    ? `${city}, ${formatGermanDate(assessedAt)}`
                                    : formatGermanDate(assessedAt)}
                            </p>
                            <div style={DSGVO_SIG_LINE_STYLE} aria-hidden="true" />
                        </div>
                        <div className="dsgvo-sig-col">
                            <div style={DSGVO_SIG_LINE_STYLE} aria-hidden="true" />
                            <p className="dsgvo-sig-label">Unterschrift Verantwortliche/r:</p>
                            <p className="dsgvo-sig-name">
                                {confirmerName.trim() || salonOwner || '____________'}
                            </p>
                            <div className="dsgvo-stamp-box" aria-hidden="true" />
                        </div>
                    </div>
                </div>
            </div>
            </div>
        </div>
    );

    const modalWidthClass = currentStep === 2 ? 'max-w-5xl' : 'max-w-3xl';

    return createPortal(
        <>
            <div
                className="fixed inset-0 bg-stone-900/70 dark:bg-black/90 z-[100000] backdrop-blur-md print:hidden"
                onClick={handleClose}
                aria-hidden="true"
            />
            <div className="fixed inset-x-0 top-[8.5rem] md:top-36 bottom-0 z-[100001] flex items-start justify-center overflow-hidden p-4 pb-8 font-sans print:inset-0 print:top-0 print:overflow-visible print:p-0">
                <div
                    className={`bg-pe-card dark:bg-[#111] rounded-2xl shadow-[0_0_40px_rgba(230,194,153,0.1)] border border-[#E6C299]/30 w-full ${modalWidthClass} max-h-[calc(100vh-9.5rem)] md:max-h-[calc(100vh-10rem)] flex min-h-0 flex-col justify-between overflow-hidden relative transition-all duration-200 my-2 print:shadow-none print:border-0 print:max-w-none print:max-h-none print:rounded-none print:my-0 print:bg-white`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <button
                        type="button"
                        onClick={handleClose}
                        className="absolute top-4 right-4 text-pe-muted dark:text-slate-400 hover:text-pe-text dark:hover:text-white bg-pe-elevated dark:bg-[#1a1a1a] rounded-lg p-2 border border-pe-border-strong dark:border-[#333] z-[60] print:hidden"
                        aria-label="Schließen"
                    >
                        <X className="w-4 h-4" />
                    </button>

                    <div className="bg-pe-elevated dark:bg-[#111622] shrink-0 print:hidden border-b border-pe-border dark:border-[#222]">
                        <div className="px-6 pt-10 pb-4">
                            <h3 className="text-lg font-bold text-pe-accent-text dark:text-[#E6C299] text-center uppercase tracking-widest pr-8 leading-snug">
                                DSGVO-Verfahrensdokumentation
                            </h3>
                            <p className="text-center text-xs text-pe-muted dark:text-slate-400 uppercase tracking-widest mt-2">
                                Schritt {currentStep} von 2: {STEP_LABELS[currentStep]}
                            </p>
                        </div>
                    </div>

                    <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 custom-scrollbar print:p-0 print:overflow-visible">
                        {currentStep === 1 && (
                            <div className="space-y-5">
                                <section className="space-y-3">
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 border-b border-pe-border dark:border-[#222] pb-2">
                                        Verantwortliche Stelle (Stammdaten)
                                    </p>
                                    <div className="rounded-xl border border-pe-border dark:border-slate-700 bg-pe-elevated dark:bg-[#0a0a0a] p-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div>
                                            <p className={LABEL_CLASS}>Salon / Firma</p>
                                            <p className="text-sm font-semibold text-pe-text dark:text-slate-100">
                                                {salonName}
                                            </p>
                                            <p className="text-xs text-pe-muted dark:text-slate-400">
                                                {salonOwner}
                                            </p>
                                            <p className="text-xs text-pe-muted dark:text-slate-400">
                                                {addressLine || 'Adresse fehlt in Stammdaten'}
                                            </p>
                                        </div>
                                        <div>
                                            <p className={LABEL_CLASS}>Kontakt</p>
                                            <p className="text-xs text-pe-text dark:text-slate-300">
                                                {email || 'E-Mail fehlt'}
                                            </p>
                                            <p className="text-xs text-pe-text dark:text-slate-300">
                                                {phone || 'Telefon fehlt'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                        <label className="block">
                                            <span className={LABEL_CLASS}>Prüfdatum</span>
                                            <input
                                                type="date"
                                                value={assessedAt}
                                                onChange={(e) => setAssessedAt(e.target.value)}
                                                className={fieldClass('assessedAt')}
                                            />
                                        </label>
                                        <label className="block">
                                            <span className={LABEL_CLASS}>Bestätigende Person</span>
                                            <input
                                                type="text"
                                                value={confirmerName}
                                                onChange={(e) => {
                                                    setConfirmerName(e.target.value);
                                                    setValidationError('');
                                                }}
                                                placeholder="Name des/der Inhaber/in"
                                                className={fieldClass('confirmerName')}
                                            />
                                        </label>
                                    </div>
                                </section>

                                <section className="space-y-3">
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 border-b border-pe-border dark:border-[#222] pb-2">
                                        Pflichtangaben (Tools / Empfänger)
                                    </p>
                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                        <div>
                                            <span className={LABEL_CLASS}>
                                                Name der Kassensoftware
                                                {kasseActive && !isOpenCashRegister ? ' *' : ''}
                                            </span>
                                            <input
                                                type="text"
                                                value={cashSoftwareName}
                                                disabled={isOpenCashRegister}
                                                onChange={(e) => {
                                                    setCashSoftwareName(e.target.value);
                                                    setValidationError('');
                                                }}
                                                placeholder="z. B. Phorest, Treatwell, Salonized…"
                                                className={`${fieldClass('cashSoftwareName')} disabled:opacity-50`}
                                            />
                                            <label className="mt-2 flex cursor-pointer items-start gap-2">
                                                <input
                                                    type="checkbox"
                                                    checked={isOpenCashRegister}
                                                    onChange={(e) => {
                                                        setIsOpenCashRegister(e.target.checked);
                                                        setValidationError('');
                                                    }}
                                                    className="mt-0.5 h-4 w-4 rounded border-pe-border text-[#E6C299]"
                                                />
                                                <span className="text-xs leading-snug text-pe-muted dark:text-slate-300">
                                                    Offene Ladenkasse (keine Software)
                                                </span>
                                            </label>
                                        </div>
                                        <div>
                                            <span className={LABEL_CLASS}>
                                                Name Steuerberater / Lohnbüro
                                                {personalActive && !isSelfAccounting ? ' *' : ''}
                                            </span>
                                            <input
                                                type="text"
                                                value={taxAdvisorName}
                                                disabled={isSelfAccounting}
                                                onChange={(e) => {
                                                    setTaxAdvisorName(e.target.value);
                                                    setValidationError('');
                                                }}
                                                placeholder="Kanzlei / Lohnbüro"
                                                className={`${fieldClass('taxAdvisorName')} disabled:opacity-50`}
                                            />
                                            <label className="mt-2 flex cursor-pointer items-start gap-2">
                                                <input
                                                    type="checkbox"
                                                    checked={isSelfAccounting}
                                                    onChange={(e) => {
                                                        setIsSelfAccounting(e.target.checked);
                                                        setValidationError('');
                                                    }}
                                                    className="mt-0.5 h-4 w-4 rounded border-pe-border text-[#E6C299]"
                                                />
                                                <span className="text-xs leading-snug text-pe-muted dark:text-slate-300">
                                                    Buchhaltung / Lohnabrechnung in Eigenregie
                                                </span>
                                            </label>
                                        </div>
                                    </div>
                                </section>

                                <div className="flex flex-wrap items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={confirmStandards}
                                        className="inline-flex items-center gap-2 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/20"
                                    >
                                        <ShieldCheck className="h-3.5 w-3.5" />
                                        Standard-Maßnahmen bestätigen
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setProcesses(
                                                buildInitialProcesses(!!clientData?.isSoloBusiness)
                                            );
                                            setToms(createDefaultDsgvoTomStates());
                                            setCashSoftwareName('');
                                            setTaxAdvisorName('');
                                            setIsOpenCashRegister(false);
                                            setIsSelfAccounting(false);
                                            setValidationError('');
                                            setInvalidFields(new Set());
                                        }}
                                        className="inline-flex items-center gap-2 rounded-xl border border-pe-border dark:border-slate-700 bg-pe-elevated dark:bg-[#0a0a0a] px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-pe-muted dark:text-slate-400 hover:text-[#E6C299]"
                                    >
                                        <RotateCcw className="h-3.5 w-3.5" />
                                        Reset
                                    </button>
                                </div>

                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setActiveTab('prozesse')}
                                        className={`flex-1 rounded-xl border px-3 py-2.5 text-[10px] font-bold uppercase tracking-wider transition-colors ${
                                            activeTab === 'prozesse'
                                                ? 'border-[#E6C299]/50 bg-[#E6C299]/15 text-pe-accent-text dark:text-[#E6C299]'
                                                : 'border-pe-border dark:border-slate-700 bg-pe-elevated dark:bg-[#0a0a0a] text-pe-muted dark:text-slate-400'
                                        }`}
                                    >
                                        1. Verarbeitungstätigkeiten
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setActiveTab('toms')}
                                        className={`flex-1 rounded-xl border px-3 py-2.5 text-[10px] font-bold uppercase tracking-wider transition-colors ${
                                            activeTab === 'toms'
                                                ? 'border-[#E6C299]/50 bg-[#E6C299]/15 text-pe-accent-text dark:text-[#E6C299]'
                                                : 'border-pe-border dark:border-slate-700 bg-pe-elevated dark:bg-[#0a0a0a] text-pe-muted dark:text-slate-400'
                                        }`}
                                    >
                                        2. TOMs (Art. 32)
                                    </button>
                                </div>

                                <div className="space-y-2">
                                    {activeTab === 'prozesse'
                                        ? DSGVO_PROCESSES.map((def) => {
                                              const state = processes.find((p) => p.id === def.id);
                                              if (!state) return null;
                                              const meta = [
                                                  def.legalBasis,
                                                  `Löschfrist: ${def.retention}`,
                                                  def.recipients ? `Empfänger: ${def.recipients}` : '',
                                              ]
                                                  .filter(Boolean)
                                                  .join(' · ');
                                              return (
                                                  <ToggleRow
                                                      key={def.id}
                                                      title={def.title}
                                                      sub={`${def.purpose} ${meta}`}
                                                      checked={state.active}
                                                      onChange={(v) => setProcessActive(def.id, v)}
                                                      note={state.note}
                                                      onNoteChange={(v) => setProcessNote(def.id, v)}
                                                  />
                                              );
                                          })
                                        : DSGVO_TOMS.map((def) => {
                                              const state = toms.find((t) => t.id === def.id);
                                              if (!state) return null;
                                              return (
                                                  <ToggleRow
                                                      key={def.id}
                                                      title={def.title}
                                                      sub={def.description}
                                                      checked={state.implemented}
                                                      onChange={(v) =>
                                                          setTomImplemented(def.id, v)
                                                      }
                                                      note={state.note}
                                                      onNoteChange={(v) => setTomNote(def.id, v)}
                                                  />
                                              );
                                          })}
                                </div>

                                {validationError ? (
                                    <p className="text-center text-xs font-semibold text-red-400">
                                        {validationError}
                                    </p>
                                ) : null}
                                <p className="text-[10px] leading-relaxed text-pe-muted dark:text-slate-500">
                                    Dokumentationshilfe für den Saloninhaber. Ersetzt keine externe
                                    Datenschutzberatung.
                                </p>
                            </div>
                        )}

                        {currentStep === 2 && (
                            <div className="bg-slate-200/40 dark:bg-black/40 rounded-xl p-2 sm:p-4">
                                {renderDocument()}
                            </div>
                        )}
                    </div>

                    {currentStep === 2 && (
                        <div className="px-6 pb-2 shrink-0 print:hidden">
                            <div
                                className={
                                    disclaimerAccepted
                                        ? 'rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-3 py-3'
                                        : 'rounded-xl border-2 border-amber-500/70 bg-amber-500/10 px-3 py-3 shadow-[0_0_0_1px_rgba(245,158,11,0.25)]'
                                }
                            >
                                {!disclaimerAccepted ? (
                                    <>
                                        <p className="mb-1 text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-amber-800 dark:text-amber-300">
                                            Pflicht · Rechtlicher Hinweis bestätigen
                                        </p>
                                        <p className="mb-2 text-[11px] sm:text-xs font-semibold text-amber-950 dark:text-amber-100/95 leading-snug">
                                            Ohne diese Bestätigung bleiben PDF-Download, Druck und
                                            Archiv-Speichern gesperrt.
                                        </p>
                                    </>
                                ) : null}
                                <label className="flex items-start gap-3 cursor-pointer group">
                                    <input
                                        type="checkbox"
                                        checked={disclaimerAccepted}
                                        onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                                        className="mt-0.5 w-7 h-7 sm:w-6 sm:h-6 shrink-0 cursor-pointer appearance-none bg-pe-input dark:bg-black border-2 border-amber-600 dark:border-amber-400 rounded-md checked:bg-[#E6C299] checked:border-[#E6C299] relative transition-colors after:content-['✔'] after:absolute after:text-black after:text-[14px] after:font-black after:left-1/2 after:top-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:opacity-0 checked:after:opacity-100"
                                    />
                                    <span
                                        className={
                                            disclaimerAccepted
                                                ? 'text-sm font-semibold text-emerald-800 dark:text-emerald-300 leading-snug'
                                                : 'text-sm text-pe-text dark:text-slate-200 leading-snug'
                                        }
                                    >
                                        {disclaimerAccepted
                                            ? 'Rechtlicher Hinweis bestätigt'
                                            : DSGVO_DISCLAIMER_CHECKBOX}
                                    </span>
                                </label>
                            </div>
                        </div>
                    )}

                    {currentStep === 2 && (
                        <div className="px-6 pt-3 print:hidden">
                            <PePapersStorageHint
                                hint={PE_PAPERS_STORAGE_HINTS.dsgvo}
                                variant="modal"
                            />
                        </div>
                    )}

                    <div className="px-6 py-4 border-t border-pe-border dark:border-[#222] shrink-0 print:hidden">
                        {currentStep === 1 && invalidFields.size > 0 && (
                            <p className="mb-3 text-center text-xs font-semibold text-red-400">
                                Bitte fülle die markierten Pflichtfelder aus.
                            </p>
                        )}
                        <div className="flex flex-wrap gap-3">
                            {currentStep > 1 && (
                                <button
                                    type="button"
                                    onClick={() => setCurrentStep(1)}
                                    className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300"
                                >
                                    Zurück zur Bearbeitung
                                </button>
                            )}
                            {currentStep === 1 ? (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => void handleArchive(true)}
                                        disabled={isSaving}
                                        className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300 disabled:opacity-50"
                                    >
                                        {archiveFeedback === 'draft'
                                            ? 'Gespeichert! ✓'
                                            : isSaving
                                              ? 'Speichert...'
                                              : 'Später fortsetzen'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleWeiter}
                                        className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest bg-[#E6C299] hover:bg-[#d9b183] text-black"
                                    >
                                        Dokument erstellen
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => void handleArchive(false)}
                                        disabled={isActionLocked || isSaving}
                                        className={btnClass(
                                            'flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border border-[#E6C299]/40 text-pe-accent-text dark:text-[#E6C299]'
                                        )}
                                    >
                                        {archiveFeedback === 'success'
                                            ? 'Gespeichert! ✓'
                                            : 'Im Archiv speichern'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void handleDownloadPdf()}
                                        disabled={isActionLocked || isPdfDownloading}
                                        className={btnClass(
                                            'flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest bg-gradient-to-b from-[#E6C299] to-[#b48a56] text-black'
                                        )}
                                    >
                                        {isPdfDownloading
                                            ? 'PDF wird erstellt...'
                                            : 'Als PDF speichern'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void handlePrint()}
                                        disabled={isActionLocked || isPdfDownloading}
                                        className={btnClass(
                                            'flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border border-[#E6C299]/40 text-pe-accent-text dark:text-[#E6C299]'
                                        )}
                                    >
                                        {isPdfDownloading ? 'Wird erstellt...' : 'Drucken'}
                                    </button>
                                </>
                            )}
                        </div>
                        {archiveFeedback === 'error' && (
                            <p className="text-[10px] text-red-400 mt-2 text-center">
                                Archivierung fehlgeschlagen. Bitte erneut versuchen.
                                {archiveErrorDetail ? (
                                    <>
                                        <br />
                                        <span className="opacity-80">{archiveErrorDetail}</span>
                                    </>
                                ) : null}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
};

export default DsgvoVvtModal;
